import { ApiService } from '../services/ApiService.js';

/**
 * @typedef {import('../types/api.d.js').ApiUsersListItem} ApiUsersListItem
 */

const CACHE_TIME = 5 * 60 * 1000;

const api = new ApiService();

class UsersStoreClass {
    /** @type {ApiUsersListItem[]} */
    #state = $state([]);
    /** @type {boolean} */
    isLoading = $state(false);
    /** @type {number} */
    #loadedAt = 0;
    /** @type {Promise<void>|null} */
    #loadingPromise = null;

    constructor() {
        void this.reset();
    }

    /**
     * @returns {ApiUsersListItem[]}
     */
    get users() {
        return this.#state;
    }

    /**
     * Lädt die Benutzerliste neu.
     *
     * @returns {Promise<void>}
     */
    async reset() {
        if (this.#loadingPromise) {
            return this.#loadingPromise;
        }

        this.isLoading = true;

        this.#loadingPromise = (async () => {
            try {
                console.log('Loading users');
                const response = await api.getUsers();

                if (response.ok) {
                    this.#state = response.data.items;
                    this.#loadedAt = Date.now();
                } else {
                    this.#state = [];
                    this.#loadedAt = 0;
                }
            } finally {
                this.isLoading = false;
                this.#loadingPromise = null;
            }
        })();

        return this.#loadingPromise;
    }

    /**
     * Lädt den Cache bei Bedarf neu.
     *
     * @returns {Promise<void>}
     */
    async #ensureLoaded() {
        if (this.#state.length === 0 || Date.now() - this.#loadedAt > CACHE_TIME) {
            await this.reset();
        }
    }

    /**
     * Sucht einen Benutzer anhand einer Bedingung.
     * Wird kein Benutzer gefunden, wird der Cache einmal aktualisiert
     * und die Suche wiederholt.
     *
     * @param {(user: ApiUsersListItem) => boolean} predicate
     * @returns {Promise<ApiUsersListItem|null>}
     */
    async #findUser(predicate) {
        await this.#ensureLoaded();

        let user = this.#state.find(predicate);

        if (user) {
            return user;
        }

        await this.reset();

        return this.#state.find(predicate) ?? null;
    }

    /**
     * Sucht einen Benutzer anhand seiner ID.
     *
     * @param {?number} userId
     * @returns {Promise<ApiUsersListItem|null>}
     */
    async findUserById(userId) {
        return this.#findUser((user) => user.userId === userId);
    }

    /**
     * Sucht einen Benutzer anhand seiner E-Mail-Adresse.
     *
     * @param {string} email
     * @returns {Promise<ApiUsersListItem|null>}
     */
    async findUserByEmail(email) {
        const search = email.trim().toLowerCase();

        return this.#findUser((user) => user.email.toLowerCase() === search);
    }
}

export const UsersStore = new UsersStoreClass();
